import type { CatalogQuery } from '@/lib/api/reports-client.js';
import { useCatalog } from '../hooks/use-catalog.js';
import { useToggleFavorite } from '../hooks/use-toggle-favorite.js';
import { CatalogEmptyState } from './catalog-empty-state.js';
import { CatalogErrorState } from './catalog-error-state.js';
import {
  CatalogFilters,
  type CatalogFiltersValue,
  categoriesFromItems,
} from './catalog-filters.js';
import { CatalogGrid } from './catalog-grid.js';
import { CatalogList } from './catalog-list.js';
import { CatalogPagination } from './catalog-pagination.js';
import { CatalogSkeleton } from './catalog-skeleton.js';

// ---------------------------------------------------------------------------
// Catalog search params shape (used by route and tests)
// ---------------------------------------------------------------------------

export interface CatalogSearchParams {
  kind?: 'report' | 'dashboard';
  category?: string;
  q?: string;
  favoritesOnly?: boolean;
  pageNumber?: number;
  pageSize?: number;
  view?: 'grid' | 'list';
}

// ---------------------------------------------------------------------------
// Build a clean CatalogQuery (no undefined values for exactOptionalPropertyTypes)
// ---------------------------------------------------------------------------

function buildCatalogQuery(
  search: CatalogSearchParams,
  pageNumber: number,
  pageSize: number,
): CatalogQuery {
  const params: CatalogQuery = { pageNumber, pageSize };
  if (search.kind !== undefined) params.kind = search.kind;
  if (search.category !== undefined && search.category.length > 0)
    params.category = search.category;
  if (search.q !== undefined && search.q.length > 0) params.q = search.q;
  if (search.favoritesOnly === true) params.favoritesOnly = true;
  return params;
}

// ---------------------------------------------------------------------------
// CatalogPage
// ---------------------------------------------------------------------------

interface CatalogPageProps {
  searchParams?: CatalogSearchParams;
  onSearchParamsChange?: (next: CatalogSearchParams, replace?: boolean) => void;
}

export function CatalogPage({ searchParams = {}, onSearchParamsChange }: CatalogPageProps) {
  const pageNumber = searchParams.pageNumber ?? 1;
  const pageSize = searchParams.pageSize ?? 50;
  const view = searchParams.view ?? 'grid';

  const queryParams = buildCatalogQuery(searchParams, pageNumber, pageSize);

  const { data, isLoading, isError, error, refetch } = useCatalog(queryParams);

  const { toggle, pendingIds } = useToggleFavorite(queryParams as Record<string, unknown>);

  const categories = data !== undefined ? categoriesFromItems(data.items) : [];

  const filterValue: CatalogFiltersValue = {
    q: searchParams.q ?? '',
    kind: searchParams.kind ?? '',
    category: searchParams.category ?? '',
    favoritesOnly: searchParams.favoritesOnly ?? false,
    view,
  };

  function buildNextParams(next: CatalogFiltersValue): CatalogSearchParams {
    const updated: CatalogSearchParams = { pageNumber: 1 };
    if (searchParams.pageSize !== undefined) updated.pageSize = searchParams.pageSize;
    if (next.q.length > 0) updated.q = next.q;
    if (next.kind.length > 0) updated.kind = next.kind as 'report' | 'dashboard';
    if (next.category.length > 0) updated.category = next.category;
    if (next.favoritesOnly) updated.favoritesOnly = true;
    if (next.view !== 'grid') updated.view = next.view;
    return updated;
  }

  function handleFiltersChange(next: CatalogFiltersValue) {
    // Filter changes replace history — no back-button pollution
    onSearchParamsChange?.(buildNextParams(next), true);
  }

  function handlePageChange(page: number) {
    // Page navigation pushes history — back/forward navigates pages
    const updated: CatalogSearchParams = { ...searchParams, pageNumber: page };
    onSearchParamsChange?.(updated, false);
  }

  function handlePageSizeChange(size: number) {
    const updated: CatalogSearchParams = { ...searchParams, pageSize: size, pageNumber: 1 };
    onSearchParamsChange?.(updated, true);
  }

  function handleClearFilters() {
    const updated: CatalogSearchParams = { pageNumber: 1 };
    if (searchParams.pageSize !== undefined) updated.pageSize = searchParams.pageSize;
    if (searchParams.view !== undefined && searchParams.view !== 'grid')
      updated.view = searchParams.view;
    onSearchParamsChange?.(updated, true);
  }

  const hasFilters =
    (searchParams.q !== undefined && searchParams.q.length > 0) ||
    searchParams.kind !== undefined ||
    (searchParams.category !== undefined && searchParams.category.length > 0) ||
    searchParams.favoritesOnly === true;

  return (
    <div className="flex flex-col gap-6">
      {/* Page heading */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-qup-text-dark)]">Catálogo</h1>
        <p className="mt-1 text-sm text-[var(--color-qup-text-muted-dark)]">
          Informes y dashboards disponibles
        </p>
      </div>

      {/* Filters */}
      <CatalogFilters value={filterValue} categories={categories} onChange={handleFiltersChange} />

      {/* Content area */}
      {isLoading && <CatalogSkeleton count={Math.min(pageSize, 12)} />}

      {isError && error !== null && (
        <CatalogErrorState error={error} onRetry={() => void refetch()} />
      )}

      {!isLoading && !isError && data !== undefined && (
        <>
          {data.items.length === 0 ? (
            <CatalogEmptyState hasFilters={hasFilters} onClearFilters={handleClearFilters} />
          ) : view === 'list' ? (
            <CatalogList items={data.items} onFavoriteToggle={toggle} pendingIds={pendingIds} />
          ) : (
            <CatalogGrid items={data.items} onFavoriteToggle={toggle} pendingIds={pendingIds} />
          )}

          {data.pagination.total_pages > 1 && (
            <CatalogPagination
              pagination={data.pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </>
      )}
    </div>
  );
}
